import React, { useEffect, useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import SelectField from '../SelectField';
import { ProspectTextField, emptyProspectForm, prospectFormSchema } from './prospectFields';

const TextField = (props) => <ProspectTextField size='medium' margin='normal' {...props} />;

const AddProspectModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  companies,
  products,
  lockedClientId = '',
  lockedProgramId = '',
}) => {
  const methods = useForm({
    mode: 'onChange',
    resolver: zodResolver(prospectFormSchema),
    defaultValues: emptyProspectForm,
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = methods;

  const selectedClientId = watch('client');
  const clientLocked = Boolean(lockedClientId);
  const programLocked = Boolean(lockedProgramId);
  const companyList = Array.isArray(companies) ? companies : [];
  const productList = Array.isArray(products) ? products : [];

  const programsForClient = useMemo(() => {
    if (!selectedClientId) return [];
    const company = companyList.find((item) => String(item?.client?._id || item?.client) === String(selectedClientId));
    if (!company?._id) return [];
    return productList.filter((program) => String(program.company?._id || program.company) === String(company._id));
  }, [companyList, productList, selectedClientId]);

  useEffect(() => {
    if (!open) return;
    reset({
      ...emptyProspectForm,
      client: lockedClientId || '',
      program: lockedProgramId || '',
    });
  }, [open, lockedClientId, lockedProgramId, reset]);

  useEffect(() => {
    if (!open || programLocked || !Array.isArray(products) || !Array.isArray(companies)) return;
    if (programsForClient.length === 1) {
      setValue('program', programsForClient[0]._id, { shouldValidate: true, shouldDirty: true });
    } else if (programsForClient.length === 0) {
      setValue('program', '');
    } else {
      const currentProgram = getValues('program');
      const stillValid = programsForClient.some((program) => String(program._id) === String(currentProgram));
      if (!stillValid) setValue('program', '');
    }
  }, [programsForClient, open, programLocked, products, companies, setValue, getValues]);

  const companiesList = useMemo(
    () =>
      companyList
        .filter((item) => item?.client?._id || item?.client)
        .map((item) => ({
          value: String(item.client?._id || item.client),
          label: item.companyName,
        })),
    [companyList],
  );

  const programsList = useMemo(
    () =>
      programsForClient.map((item) => ({
        value: item._id,
        label: item.productName,
      })),
    [programsForClient],
  );

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} disableRestoreFocus fullWidth maxWidth='md'>
      <DialogTitle>Add prospect</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Typography variant='body2' color='text.secondary'>
              Add a prospect and submit it for client approval. Once approved it will become available in Engage.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 2,
                alignItems: 'start',
              }}
            >
              <SelectField
                label='Client'
                name='client'
                options={companiesList}
                disabled={clientLocked || isSubmitting}
              />
              <Box>
                <SelectField
                  label='Program'
                  name='program'
                  options={programsList}
                  disabled={programLocked || isSubmitting || !selectedClientId || programsList.length === 0}
                />
                {selectedClientId && Array.isArray(products) && programsList.length === 0 ? (
                  <Typography variant='caption' color='error' display='block' mb={1}>
                    This client has no programs. A program is required to submit a prospect.
                  </Typography>
                ) : null}
              </Box>
              <TextField label='Prospect company' name='company' disabled={isSubmitting} />
              <TextField label='Contact name' name='contactName' disabled={isSubmitting} />
              <TextField
                label='Title (optional)'
                name='title'
                disabled={isSubmitting}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
              <TextField label='Website (optional)' name='website' disabled={isSubmitting} />
              <TextField label='LinkedIn (optional)' name='linkedin' disabled={isSubmitting} />
            </Box>
            <TextField
              label='How do you know this person?'
              name='howDoYouKnowThisPerson'
              multiline
              minRows={2}
              disabled={isSubmitting}
            />
            <TextField
              label='Why do you think this could be relevant?'
              name='whyRelevant'
              multiline
              minRows={2}
              disabled={isSubmitting}
            />
            <TextField
              label='Anything else I should know?'
              name='anythingElse'
              multiline
              minRows={2}
              disabled={isSubmitting}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mt: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: '#eef5ff',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', mt: '1px' }} />
              <Typography variant='body2' color='text.secondary'>
                This prospect must be approved by the client before Engage becomes available.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color='secondary' disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={!isValid || isSubmitting}>
              Submit for approval
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddProspectModal;
